import { useState, useEffect } from "react";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { Percent, DollarSign, Calendar, Plus, Trash2, Users, BarChart3, Tag, CheckCircle, XCircle, X, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface Discount {
  id: string;
  code: string;
  type: "percentage" | "fixed";
  value: number;
  expiresAt: any;
  isActive: boolean;
  createdAt: any;
  instructorId: string;
  usageCount: number;
}

interface DiscountUsage {
  id: string;
  discountId: string;
  userId: string;
  userEmail: string;
  courseId: string;
  courseName: string;
  usedAt: any;
  discountAmount: number;
}

export default function DiscountManagement() {
  const { user } = useAuthContext();
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [usages, setUsages] = useState<DiscountUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(null);
  
  // Form states
  const [code, setCode] = useState("");
  const [type, setType] = useState<"percentage" | "fixed">("percentage");
  const [value, setValue] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadDiscounts();
  }, [user]);

  async function loadDiscounts() {
    if (!user) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, "discounts"),
        where("instructorId", "==", user.uid)
      );
      const snap = await getDocs(q);
      
      const discountsList: Discount[] = [];
      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        
        // Count usage
        const usageQuery = query(
          collection(db, "discountUsages"),
          where("discountId", "==", docSnap.id)
        );
        const usageSnap = await getDocs(usageQuery);
        
        discountsList.push({
          id: docSnap.id,
          code: data.code,
          type: data.type,
          value: data.value,
          expiresAt: data.expiresAt,
          isActive: data.isActive,
          createdAt: data.createdAt,
          instructorId: data.instructorId,
          usageCount: usageSnap.docs.length
        });
      }

      // Sort by creation date (newest first)
      discountsList.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setDiscounts(discountsList);
    } catch (error) {
      console.error("Error loading discounts:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadUsages(discountId: string) {
    try {
      const usageQuery = query(
        collection(db, "discountUsages"),
        where("discountId", "==", discountId)
      );
      const usageSnap = await getDocs(usageQuery);
      
      const usagesList: DiscountUsage[] = usageSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          discountId: data.discountId,
          userId: data.userId,
          userEmail: data.userEmail || "ไม่ระบุ",
          courseId: data.courseId,
          courseName: data.courseName || "ไม่ทราบชื่อคอร์ส",
          usedAt: data.usedAt,
          discountAmount: data.discountAmount
        };
      });

      // Sort by usage date (newest first)
      usagesList.sort((a, b) => {
        const timeA = a.usedAt?.toMillis?.() || 0;
        const timeB = b.usedAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setUsages(usagesList);
    } catch (error) {
      console.error("Error loading usages:", error);
    }
  }

  async function handleCreateDiscount() {
    if (!user || !code.trim() || !value || !expiresAt) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) {
      alert("กรุณากรอกมูลค่าส่วนลดที่ถูกต้อง");
      return;
    }

    if (type === "percentage" && numValue > 100) {
      alert("เปอร์เซ็นต์ส่วนลดต้องไม่เกิน 100%");
      return;
    }

    try {
      setCreating(true);

      // Check if code already exists
      const existingQuery = query(
        collection(db, "discounts"),
        where("code", "==", code.trim().toUpperCase()),
        where("instructorId", "==", user.uid)
      );
      const existingSnap = await getDocs(existingQuery);
      
      if (!existingSnap.empty) {
        alert("รหัสส่วนลดนี้มีอยู่แล้ว กรุณาใช้รหัสอื่น");
        return;
      }

      await addDoc(collection(db, "discounts"), {
        code: code.trim().toUpperCase(),
        type,
        value: numValue,
        expiresAt: new Date(expiresAt),
        isActive: true,
        createdAt: serverTimestamp(),
        instructorId: user.uid
      });

      // Reset form
      setCode("");
      setValue("");
      setExpiresAt("");
      setShowCreateModal(false);
      
      // Reload discounts
      await loadDiscounts();
      alert("สร้างรหัสส่วนลดสำเร็จ!");
    } catch (error) {
      console.error("Error creating discount:", error);
      alert("เกิดข้อผิดพลาดในการสร้างรหัสส่วนลด");
    } finally {
      setCreating(false);
    }
  }

  async function handleToggleActive(discount: Discount) {
    try {
      await updateDoc(doc(db, "discounts", discount.id), {
        isActive: !discount.isActive
      });
      await loadDiscounts();
    } catch (error) {
      console.error("Error toggling discount:", error);
      alert("เกิดข้อผิดพลาด");
    }
  }

  async function handleDeleteDiscount(discountId: string) {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะลบรหัสส่วนลดนี้?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "discounts", discountId));
      await loadDiscounts();
      alert("ลบรหัสส่วนลดสำเร็จ!");
    } catch (error) {
      console.error("Error deleting discount:", error);
      alert("เกิดข้อผิดพลาดในการลบ");
    }
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "ไม่ทราบวันที่";
    const date = timestamp.toDate?.() || new Date(timestamp);
    return new Intl.DateTimeFormat("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric"
    }).format(date);
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return "ไม่ทราบวันที่";
    const date = timestamp.toDate?.() || new Date(timestamp);
    return new Intl.DateTimeFormat("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  const isExpired = (expiresAt: any) => {
    if (!expiresAt) return false;
    const expireDate = expiresAt.toDate?.() || new Date(expiresAt);
    return expireDate < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
            <ArrowLeft className="w-5 h-5" />
            กลับไปหน้า Dashboard
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
              <p className="text-textLight text-lg">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Back Button */}
      <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
        <ArrowLeft className="w-5 h-5" />
        กลับไปหน้า Dashboard
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              จัดการส่วนลด
            </h1>
            <p className="text-gray-600 mt-2">สร้างและจัดการโค้ดส่วนลดสำหรับโปรโมชั่น</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            สร้างรหัสส่วนลด
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Tag className="w-8 h-8" />
              <p className="text-sm opacity-90">รหัสทั้งหมด</p>
            </div>
            <p className="text-4xl font-black">{discounts.length}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8" />
              <p className="text-sm opacity-90">ใช้งานได้</p>
            </div>
            <p className="text-4xl font-black">
              {discounts.filter(d => d.isActive && !isExpired(d.expiresAt)).length}
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8" />
              <p className="text-sm opacity-90">ถูกใช้ทั้งหมด</p>
            </div>
            <p className="text-4xl font-black">
              {discounts.reduce((sum, d) => sum + d.usageCount, 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Discounts List */}
      {discounts.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">ยังไม่มีรหัสส่วนลด</h3>
          <p className="text-gray-500 mb-6">เริ่มสร้างรหัสส่วนลดเพื่อทำโปรโมชั่นให้กับนักเรียน</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            สร้างรหัสส่วนลด
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {discounts.map((discount) => {
            const expired = isExpired(discount.expiresAt);
            return (
              <div
                key={discount.id}
                className="bg-white rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 border-2 border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      discount.type === "percentage"
                        ? "bg-gradient-to-br from-blue-500 to-pink-500"
                        : "bg-gradient-to-br from-green-500 to-emerald-500"
                    }`}>
                      {discount.type === "percentage" ? (
                        <Percent className="w-6 h-6 text-white" />
                      ) : (
                        <DollarSign className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-gray-900">{discount.code}</h3>
                      <p className="text-sm text-gray-500">
                        {discount.type === "percentage" 
                          ? `ลด ${discount.value}%` 
                          : `ลด ฿${discount.value.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleActive(discount)}
                      className={`p-2 rounded-lg transition-all ${
                        discount.isActive && !expired
                          ? "bg-green-100 text-green-600 hover:bg-green-200"
                          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                      }`}
                      title={discount.isActive ? "ปิดใช้งาน" : "เปิดใช้งาน"}
                    >
                      {discount.isActive && !expired ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteDiscount(discount.id)}
                      className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all"
                      title="ลบ"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>หมดอายุ: {formatDate(discount.expiresAt)}</span>
                    {expired && (
                      <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-semibold">
                        หมดอายุแล้ว
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>ถูกใช้ไปแล้ว: {discount.usageCount} ครั้ง</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedDiscount(discount);
                    loadUsages(discount.id);
                    setShowUsageModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <BarChart3 className="w-5 h-5" />
                  ดูรายงานการใช้งาน
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">สร้างรหัสส่วนลด</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  รหัสส่วนลด
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="เช่น: NEWYEAR2024"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ประเภทส่วนลด
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setType("percentage")}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      type === "percentage"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Percent className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-sm font-semibold">เปอร์เซ็นต์</p>
                  </button>
                  <button
                    onClick={() => setType("fixed")}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      type === "fixed"
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <DollarSign className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-sm font-semibold">ยอดเงิน</p>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  มูลค่าส่วนลด {type === "percentage" ? "(%)" : "(บาท)"}
                </label>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={type === "percentage" ? "เช่น: 20" : "เช่น: 500"}
                  min="0"
                  max={type === "percentage" ? "100" : undefined}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  วันหมดอายุ
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleCreateDiscount}
                disabled={creating}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              >
                {creating ? "กำลังสร้าง..." : "สร้างรหัส"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Report Modal */}
      {showUsageModal && selectedDiscount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-8 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">รายงานการใช้งาน</h3>
                  <p className="text-gray-600 mt-1">รหัส: {selectedDiscount.code}</p>
                </div>
                <button
                  onClick={() => {
                    setShowUsageModal(false);
                    setSelectedDiscount(null);
                    setUsages([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto max-h-[calc(80vh-120px)]">
              {usages.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">ยังไม่มีการใช้งานรหัสนี้</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {usages.map((usage) => (
                    <div
                      key={usage.id}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">{usage.courseName}</h4>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><span className="font-semibold">อีเมล:</span> {usage.userEmail}</p>
                            <p><span className="font-semibold">ส่วนลด:</span> <span className="text-green-600 font-bold">฿{usage.discountAmount.toLocaleString()}</span></p>
                            <p><span className="font-semibold">วันที่ใช้:</span> {formatDateTime(usage.usedAt)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
